import { useCallback, useEffect, useRef, useState } from "react";
import {
    speechRecognitionConstructor,
    type SpeechRecognitionLike,
} from "../../../shared/domain/speechRecognition";
import {
    normalizeDictatedText,
    type DictationLanguage,
} from "../../../shared/domain/dictationText";

export type NotesDictationOptions = {
    /** The language the therapist speaks, as the profile stores it. */
    language: DictationLanguage;
    /**
     * What is being heard right now, shown provisionally. Each call replaces
     * the previous provisional text; "" clears it.
     */
    onInterim: (text: string) => void;
    /** A phrase that has settled, to be written in place of the provisional text. */
    onFinal: (text: string) => void;
};

export type NotesDictation = {
    /** False where the browser has no recogniser (Firefox) — the control cannot work. */
    supported: boolean;
    isDictating: boolean;
    start: () => void;
    stop: () => void;
    toggle: () => void;
};

/**
 * Dictating session notes.
 *
 * Two things about the Web Speech API make a naive reading of its events write
 * every sentence twice, which is exactly how it was being read here:
 *
 * 1. `results` is cumulative and `resultIndex` can point back at a phrase that
 *    already settled in an earlier event. Writing everything from
 *    `resultIndex` onwards writes those phrases again — so the hook tracks how
 *    many results it has already settled and never goes back over them.
 * 2. A settled phrase *supersedes* the provisional text it was forming, it
 *    does not follow it. The caller is told to replace, never to append, and
 *    the provisional text is cleared on the same event that settles it.
 *
 * The API also ends a session on its own after a silence; restarting it is
 * what makes dictation feel continuous, and a restarted session numbers its
 * results from zero again, so the settled count resets with it.
 */
export function useNotesDictation({
    language,
    onInterim,
    onFinal,
}: NotesDictationOptions): NotesDictation {
    const [isDictating, setIsDictating] = useState(false);
    const [supported] = useState(() => speechRecognitionConstructor() !== null);

    const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
    /** Whether the last end was a silence or the therapist pressing stop. */
    const wantsToListenRef = useRef(false);
    /** How many results of this recognition session have already been written. */
    const settledCountRef = useRef(0);

    // Read from the recogniser's callbacks, which are built once per session
    // and would otherwise keep calling the handlers of the render that built them.
    const handlersRef = useRef({ language, onInterim, onFinal });
    useEffect(() => {
        handlersRef.current = { language, onInterim, onFinal };
    }, [language, onInterim, onFinal]);

    const start = useCallback(() => {
        const Recognition = speechRecognitionConstructor();
        if (!Recognition) return;

        const recognition = new Recognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = handlersRef.current.language === "es" ? "es-ES" : "en-US";
        settledCountRef.current = 0;

        recognition.onstart = () => setIsDictating(true);

        recognition.onresult = (event) => {
            const { language: spoken, onInterim: interim, onFinal: final } =
                handlersRef.current;
            let pending = "";

            // Never re-read a result that has already been written, however far
            // back this event claims to start.
            const firstUnsettled = Math.max(event.resultIndex, settledCountRef.current);
            for (let index = firstUnsettled; index < event.results.length; index += 1) {
                const result = event.results[index];
                const heard = result?.[0]?.transcript ?? "";
                if (result?.isFinal) {
                    settledCountRef.current = index + 1;
                    const settled = normalizeDictatedText(heard, spoken);
                    if (settled) final(settled);
                } else {
                    pending += heard;
                }
            }

            interim(normalizeDictatedText(pending, spoken));
        };

        recognition.onerror = (event) => {
            // A silence is not a failure: the session ends and the next one
            // starts, which is what keeps a long dictation going.
            if (event.error === "no-speech" || event.error === "aborted") return;
            console.error("Speech recognition error", event.error);
            wantsToListenRef.current = false;
            handlersRef.current.onInterim("");
            setIsDictating(false);
        };

        recognition.onend = () => {
            handlersRef.current.onInterim("");
            // The next session numbers its results from zero.
            settledCountRef.current = 0;
            if (wantsToListenRef.current) {
                try {
                    recognition.start();
                    return;
                } catch {
                    // Some builds refuse an immediate restart; show the control
                    // as stopped rather than claim to be listening.
                }
            }
            wantsToListenRef.current = false;
            setIsDictating(false);
        };

        recognitionRef.current = recognition;
        wantsToListenRef.current = true;
        try {
            recognition.start();
            // Safari does not always fire `onstart`.
            setIsDictating(true);
        } catch (error) {
            console.error("Failed to start speech recognition", error);
            wantsToListenRef.current = false;
            setIsDictating(false);
        }
    }, []);

    const stop = useCallback(() => {
        wantsToListenRef.current = false;
        handlersRef.current.onInterim("");
        recognitionRef.current?.stop();
        setIsDictating(false);
    }, []);

    const toggle = useCallback(() => {
        if (wantsToListenRef.current) {
            stop();
            return;
        }
        start();
    }, [start, stop]);

    // Leaving the screen must not leave the microphone open.
    useEffect(
        () => () => {
            wantsToListenRef.current = false;
            recognitionRef.current?.abort();
            recognitionRef.current = null;
        },
        []
    );

    return { supported, isDictating, start, stop, toggle };
}
